<template>
  <div
    class="colour-picker-option"
    :class="{ 'selected-colour-picker': this.isSelected }"
    @click="onSelectPalette"
    :style="{ background: `${colourPreviewHex}` }"
  ></div>
</template>

<script>
import { OutfitActionTypeEnum } from '@/enum/outfit_action_type';
import { TraitActionTypeEnum } from '@/enum/trait_action_type';
import { MixAndMatchCategoryEnum } from '@/enum/mix_and_match';
import MixAndMatchHelper from '@/services/MixAndMatchHelper';
import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import { ColourPickerOptionsType } from '@/services/ColourPickerOptionsHelper';

export default {
  name: 'ColourPickerOption',
  props: [
    'colourPalette', //: IColourGroupTone, string, number[]
    'isSelected', //: boolean
    'secondaryColours', //: optional number[]
    'colourPickerOptionsType', //: ColourPickerOptionsType
  ],
  computed: {
    isFacialFeature: function() {
      return FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(
        this.$store.getters.selectedCategory,
      );
    },
    colourPreviewHex: function() {
      switch (this.colourPickerOptionsType) {
        case ColourPickerOptionsType.FACIAL_FEATURES:
          return this.colourPreviewHexFacialFeature;
        case ColourPickerOptionsType.TRAITS:
          return this.colourPreviewHexTrait;
        case ColourPickerOptionsType.FASHION:
        default:
          return this.colourPreviewHexFashion;
      }
    },
    colourPreviewHexFashion: function() {
      const colourCategory = MixAndMatchHelper.getItemCategory(
        this.$store.getters.selectedCategory,
      );
      const firstColourKey = colourCategory + '_tone1';
      const hexColour = this.colourPalette[firstColourKey];
      let secondHexColour;
      let thirdHexColour;

      if (this.secondaryColours) {
        if (this.secondaryColours[0]) {
          const secondColourKey = colourCategory + `_tone${this.secondaryColours[0] + 1}`;
          secondHexColour = this.colourPalette[secondColourKey];
        }

        if (this.secondaryColours[1]) {
          const thirdColourKey = colourCategory + `_tone${this.secondaryColours[1] + 1}`;
          thirdHexColour = this.colourPalette[thirdColourKey];
        }
      }

      return this.getIconForColourHexValues(hexColour, secondHexColour, thirdHexColour);
    },
    colourPreviewHexFacialFeature: function() {
      const groupId = this.$store.getters.facialFeatureSelectedGroupId;
      const metadata = this.$store.getters.facialFeaturesMetadata;
      const colour = metadata.groups[groupId].colour_options.find((colour) => {
        return colour.id === this.colourPalette;
      });

      const iconColours = colour.icon_colours.value;

      if (iconColours.length <= 0) {
        // This shouldn't happen
        return `#000000`;
      }

      // Compute hex colours for icons. Include secondary/tertiary colours if present
      const hexColour = iconColours[0];
      const secondHexColour = iconColours[1];
      const thirdHexColour = iconColours[2];

      return this.getIconForColourHexValues(hexColour, secondHexColour, thirdHexColour);
    },
    colourPreviewHexTrait: function() {
      const hexColour = this.colourPalette[0];
      let secondHexColour;
      let thirdHexColour;

      if (this.secondaryColours) {
        secondHexColour = this.secondaryColours[0] && this.colourPalette[this.secondaryColours[0]];
        thirdHexColour = this.secondaryColours[1] && this.colourPalette[this.secondaryColours[1]];
      }
      return this.getIconForColourHexValues(hexColour, secondHexColour, thirdHexColour);
    },
  },
  methods: {
    onSelectPalette: function() {
      switch (this.colourPickerOptionsType) {
        case ColourPickerOptionsType.FACIAL_FEATURES:
          return this.onSelectPaletteFacialFeature();
        case ColourPickerOptionsType.TRAITS:
          return this.onSelectPaletteTrait();
        case ColourPickerOptionsType.FASHION:
        default:
          return this.onSelectPaletteFashion();
      }
    },
    onSelectPaletteFashion: function() {
      const colourCategory = MixAndMatchHelper.getItemCategory(
        this.$store.getters.selectedCategory,
      );
      const initialTraits = Object.assign({}, this.$store.getters.traits);
      // Picking a colour for a regular garment
      // set character data
      this.$store.commit('setColourPaletteForMixAndMatchItem', {
        category: colourCategory,
        colourPalette: this.colourPalette,
      });
      // set selected colour palette for item to stay consistent
      this.$store.commit('setSelectedColourPalettesForMixAndMatchItems', {
        category: colourCategory,
        itemId: this.$store.getters.traits[colourCategory],
        colourPalette: this.colourPalette,
      });

      // Picking a colour for a bodysuit
      const currentBodysuit = MixAndMatchHelper.getCurrentBodysuit(
        this.$store.getters.traits.top,
        this.$store.getters.categories,
      );
      const isWearingFullBodysuit = MixAndMatchHelper.isWearingFullBodysuit(
        this.$store.getters.traits.bottom,
        this.$store.getters.traits.top,
        this.$store.getters.categories,
      );

      // Handle bodysuits colours
      if (currentBodysuit && this.$store.getters.selectedCategory === MixAndMatchCategoryEnum.TOP) {
        this.handleSelectBodysuitColourPalette(isWearingFullBodysuit);
      }

      // Handle one pieces colours
      if (
        this.$store.getters.mixAndMatchMetadata.one_pieces[this.$store.getters.traits.top] &&
        colourCategory === MixAndMatchCategoryEnum.TOP
      ) {
        this.handleSelectBodysuitColourPalette(true);
      }

      this.$store.commit('updateAvatarActions', {
        initialTraits,
      });

      // Log metrics
      this.$store.commit('logOutfitAction', {
        action: OutfitActionTypeEnum.TAP_COLOUR,
      });
    },
    handleSelectBodysuitColourPalette: function(isWearingFullBodysuit) {
      const colourCategory = MixAndMatchHelper.getItemCategory(
        this.$store.getters.selectedCategory,
      );
      // Only change the colour of the bottom if only wearing full bodysuit
      let colourPaletteWithBottomKeys = {};
      Object.keys(this.colourPalette).forEach((tone) => {
        const newBottomKey = tone.replace(
          MixAndMatchCategoryEnum.TOP,
          MixAndMatchCategoryEnum.BOTTOM,
        );
        colourPaletteWithBottomKeys[newBottomKey] = this.colourPalette[tone];
      });

      if (isWearingFullBodysuit) {
        this.$store.commit('setColourPaletteForMixAndMatchItem', {
          category: MixAndMatchCategoryEnum.BOTTOM,
          colourPalette: colourPaletteWithBottomKeys,
        });
      }

      // However we want to save the colour the is being selected to the bodysuit bottom
      // even if we are not currently wearing a full body to stay consistant
      this.$store.commit('setSelectedColourPalettesForMixAndMatchItems', {
        category: MixAndMatchCategoryEnum.BOTTOM,
        itemId: this.$store.getters.traits[colourCategory],
        colourPalette: Object.assign(colourPaletteWithBottomKeys, this.colourPalette),
      });
    },
    onSelectPaletteFacialFeature: function() {
      const category = this.$store.getters.selectedCategory;
      const colour = this.colourPalette;
      const groupId = this.$store.getters.facialFeatureSelectedGroupId;

      const initialTraits = Object.assign({}, this.$store.getters.traits);
      const initialFacialFeatureTraits = Object.assign({}, this.$store.getters.facialFeatureTraits);

      // If ColourPickerOption renders that means there must be a groupId that is selected. That means even if the group
      //  is a dual earring, the groupIds will be the same
      this.$store.commit('setFacialFeature', {
        category,
        value: groupId,
        colour,
      });
      this.$store.commit('updateAvatarActions', {
        initialTraits,
        facialFeatureTraits: initialFacialFeatureTraits,
      });
      this.$store.commit('logTraitAction', {
        action: TraitActionTypeEnum.TAP_PIERCING_COLOUR,
      });
    },
    onSelectPaletteTrait: function() {
      const category = this.$store.getters.selectedCategory;
      const initialTraits = Object.assign({}, this.$store.getters.traits);

      this.$store.commit('setTraitColourOption', {
        category,
        colourOption: this.colourPalette,
      });
      this.$store.commit('updateAvatarActions', {
        initialTraits,
      });

      // TODO: 'logTraitAction' "TAP_COLOUR" action (requires native side work)
    },
    formatIntColourValueToHex: function(colourIntValue) {
      return colourIntValue.toString(16);
    },
    getIconForColourHexValues: function(...colourIntValues) {
      // Filter out missing or empty values, convert to hex
      const formattedColourHexValues = colourIntValues
        .filter((colourInt) => !!colourInt)
        .map((colourInt) => this.formatIntColourValueToHex(colourInt));

      // We only support up to 3 colour hex values for the icon.
      // Also note that some conversions don't add leading 0's resulting in hex value with less than 6 characters.
      // We need to pad them as 6 characters hex value is expected
      if (formattedColourHexValues.length === 1) {
        return `#${formattedColourHexValues[0].padStart(6, '0')}`;
      } else if (formattedColourHexValues.length === 2) {
        return `linear-gradient(
          135deg,
          #${formattedColourHexValues[0].padStart(6, '0')} 62.5%,
          #${formattedColourHexValues[1].padStart(6, '0')} 62.5%
        )`;
      } else if (formattedColourHexValues.length === 3) {
        return `linear-gradient(
            135deg,
            #${formattedColourHexValues[0].padStart(6, '0')} 0 50%,
            #${formattedColourHexValues[1].padStart(6, '0')} 50% 65.4%,
            #${formattedColourHexValues[2].padStart(6, '0')} 65.4% 100%
          )`;
      }
    },
  },
};
</script>

<style scoped>
.selected-checkmark {
  height: 60%;
  width: 60%;
}

.selected-checkmark img {
  width: 100%;
  height: 100%;
}

.selected-colour-picker::before {
  content: '';
  display: block;
  position: absolute;
  top: -6px;
  left: -6px;
  width: 32px;
  height: 32px;
  z-index: -1;
  background: rgba(0, 0, 0, 0.26);
  border: 2px solid #fff;
  border-radius: 18px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.26);
}
</style>
